import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Play, 
  Target, 
  Users, 
  TrendingUp, 
  Building2,
  AlertTriangle,
  Zap,
  Shield,
  Clock,
  Wrench,
  DollarSign,
  TrendingDown,
  Layers,
  ChevronDown,
  ChevronUp,
  Check,
  ArrowRight
} from "lucide-react";
import { getScenario, startSimulation, PERSONA_IMAGES } from "@/lib/api";

const PERSONA_DETAILS = {
  jamie_sterling: {
    background: "Former investment banker with deep expertise in M&A and financial modeling. Jamie is a deal-maker, not an operator, which is why Innovatech's operational challenges are outside his comfort zone.",
    simulation_experience: "Expect a fast-paced, direct conversation. Jamie will cut through fluff and demand quantified ROI, proven track records, and fast timelines. He's skeptical of consultants and will push back on vague value propositions. Win him over with financial specifics and speed.",
    difficulty: "Hard",
    focus_areas: ["Metrics (M)", "Economic Buyer (E)"],
    trust_triggers: {
      positive: ["Quantified ROI", "Fast timeline", "Proven track record"],
      negative: ["Vague promises", "Consultant jargon", "Long explanations"]
    }
  },
  evelyn_reed: {
    background: "PhD in Industrial Engineering and former COO of a major manufacturing conglomerate. Evelyn was brought into Helios to provide operational oversight and feels her advice was ignored during the initial Innovatech due diligence.",
    simulation_experience: "A more methodical conversation focused on process and evidence. Evelyn will ask detailed 'how' questions about your vetting methodology, operator track records, and risk mitigation. She can become your Champion if you earn her trust through substance.",
    difficulty: "Medium",
    focus_areas: ["Decision Criteria (D1)", "Champion (C1)"],
    trust_triggers: {
      positive: ["Detailed methodology", "Evidence & data", "Acknowledging complexity"],
      negative: ["Oversimplification", "Ignoring her expertise", "Lack of due diligence"]
    }
  },
  ben_carter: {
    background: "Career procurement professional focused on cost reduction, vendor standardization, and contract compliance. Ben views specialized networks as overpriced and prefers working with established vendors where Helios has rate cards.",
    simulation_experience: "A challenging conversation centered on price, terms, and process. Ben will try to commoditize your service and push for discounts. You'll need to justify premium positioning while showing flexibility on contract terms. Difficult to convert but important for deal closure.",
    difficulty: "Very Hard",
    focus_areas: ["Paper Process (P)", "Competition (C2)"],
    trust_triggers: {
      positive: ["Clear pricing", "Contract flexibility", "Risk mitigation"],
      negative: ["Unclear costs", "Rigid terms", "Dismissing procurement"]
    }
  }
};

export const LandingPage = () => {
  const navigate = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState("jamie_sterling");
  const [expandedPersona, setExpandedPersona] = useState(null);
  const [expandedScenario, setExpandedScenario] = useState(false);
  const [expandedMission, setExpandedMission] = useState(false);
  const [expandedProblem, setExpandedProblem] = useState(false);

  useEffect(() => {
    loadScenario();
  }, []);

  const loadScenario = async () => {
    try {
      const response = await getScenario();
      setScenario(response.data);
    } catch (error) {
      console.error("Failed to load scenario:", error);
      toast.error("Failed to load scenario data");
    } finally {
      setLoading(false);
    }
  };

  const handleStartSimulation = async () => {
    setStarting(true);
    try {
      const response = await startSimulation("helios_capital", selectedPersona);
      const { session_id } = response.data;
      toast.success("Simulation started!");
      navigate(`/simulation/${session_id}`);
    } catch (error) {
      console.error("Failed to start simulation:", error);
      toast.error("Failed to start simulation");
      setStarting(false);
    }
  };

  const toggleExpanded = (personaId) => {
    setExpandedPersona(expandedPersona === personaId ? null : personaId);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "Medium": return "bg-amber-50 text-amber-700 border-amber-200";
      case "Hard": return "bg-orange-50 text-orange-700 border-orange-200";
      case "Very Hard": return "bg-red-50 text-red-700 border-red-200";
      default: return "bg-slate-50 text-slate-700 border-slate-200";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-slate-400">Loading simulation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header with bold accent border */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50 accent-border-top">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Deal Velocity</h1>
                <p className="text-xs text-slate-400">AI Sales Simulation</p>
              </div>
            </div>
            <Button 
              onClick={handleStartSimulation}
              disabled={starting}
              data-testid="start-simulation-btn"
              className="bg-primary hover:bg-primary/90 text-white font-semibold px-5"
            >
              {starting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Play className="w-4 h-4 mr-1.5" />
                  Start Simulation
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-10">
        
        {/* Hero Section */}
        <div className="mb-10">
          <Badge className="mb-3 bg-primary/10 text-primary hover:bg-primary/10 border-primary/20 font-medium">
            Scenario Briefing
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
            The Helios Capital Opportunity
          </h2>
          <p className="text-base text-slate-500 max-w-2xl">
            Enter a sales simulation with Helios Capital Partners executives. 
            Apply MEDDPICC to navigate deal dynamics and secure the next step.
          </p>
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-5 gap-8 mb-10">
          
          {/* Left Column - Client Intel */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Client Overview Card */}
            <Card className="overflow-hidden border-slate-200">
              <div className="bg-slate-900 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white">Helios Capital Partners</h3>
                      <p className="text-sm text-slate-400">$2B Mid-Market Private Equity Fund</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedScenario(!expandedScenario)}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                    data-testid="read-more-scenario"
                  >
                    {expandedScenario ? (
                      <><ChevronUp className="w-4 h-4 mr-1" />Less</>
                    ) : (
                      <><ChevronDown className="w-4 h-4 mr-1" />How PE Works</>
                    )}
                  </Button>
                </div>
              </div>
              <CardContent className="p-6">
                {expandedScenario && (
                  <div className="mb-6 p-5 bg-slate-50 rounded-xl border border-slate-200 animate-fade-in-up">
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-primary" />
                      How Private Equity Works
                    </h4>
                    <div className="space-y-4 text-sm text-slate-600">
                      <div>
                        <p className="font-medium text-slate-800 mb-1">The PE Business Model</p>
                        <p>Private Equity firms like Helios raise capital from institutional investors (Limited Partners) to buy and improve companies over 3-7 years. Their goal is to increase the company's value -- primarily measured by EBITDA -- and then sell for a profit. Time is critical: faster improvements mean higher returns (IRR).</p>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="p-3 bg-white rounded-lg border border-blue-100">
                          <p className="font-medium text-primary mb-1">Pre-Acquisition Phase</p>
                          <p className="text-xs text-slate-500">During due diligence, PE firms need industry experts to validate investment theses, assess operational risks, and identify value creation opportunities.</p>
                        </div>
                        <div className="p-3 bg-white rounded-lg border border-emerald-100">
                          <p className="font-medium text-emerald-700 mb-1">Post-Acquisition Phase</p>
                          <p className="text-xs text-slate-500">After acquisition, PE firms execute a "100-day plan" to drive improvements. They often lack the operational expertise internally.</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 mb-1">The Expertise Gap</p>
                        <p>PE professionals are financial experts -- skilled at M&A, deal structuring, and capital allocation. But when portfolio companies face operational challenges, they need seasoned operators who have "done it before" to step in and execute.</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="data-label mb-2">Investment Model</p>
                    <p className="text-sm text-slate-600">
                      Finance experts driving value through <span className="font-semibold text-primary">operational improvements</span> in tech & industrials.
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                    <p className="data-label mb-2 text-primary">Your Opening</p>
                    <p className="text-sm text-slate-600">
                      They have capital but need <span className="font-semibold text-primary">proven operators</span> to create value.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* The Problem Card */}
            <Card className="border-orange-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-orange-600" />
                    </div>
                    <CardTitle className="text-lg text-orange-700">The Urgent Problem: Innovatech Dynamics</CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedProblem(!expandedProblem)}
                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-7 text-xs"
                    data-testid="read-more-problem"
                  >
                    {expandedProblem ? (
                      <><ChevronUp className="w-3 h-3 mr-1" />Less</>
                    ) : (
                      <><ChevronDown className="w-3 h-3 mr-1" />Full Story</>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {expandedProblem && (
                  <div className="mb-4 p-5 bg-white rounded-xl border border-orange-100 animate-fade-in-up">
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-orange-600" />
                      The Innovatech Dynamics Story
                    </h4>
                    <div className="space-y-4 text-sm text-slate-600">
                      <div>
                        <p className="font-medium text-slate-800 mb-1">The Company</p>
                        <p>Innovatech Dynamics is a mid-sized industrial sensor manufacturer specializing in IoT-enabled monitoring solutions for manufacturing and logistics. With $75M in revenue, they serve Fortune 500 clients in automotive, aerospace, and industrial automation sectors.</p>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                          <p className="font-medium text-primary mb-1">The Acquisition</p>
                          <p className="text-xs text-slate-500">Six months ago, Helios acquired Innovatech for $180M, seeing an opportunity to consolidate the fragmented industrial IoT market.</p>
                        </div>
                        <div className="p-3 bg-orange-50/50 rounded-lg border border-orange-100">
                          <p className="font-medium text-orange-700 mb-1">The Hidden Problem</p>
                          <p className="text-xs text-slate-500">Innovatech had acquired SensorTech and never properly integrated it. Two separate supply chains, duplicate vendors, incompatible systems.</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 mb-1">The Crisis Unfolds</p>
                        <p>Post-acquisition, the cracks became visible. Supply chain costs spiraled. Key customers began complaining. The 100-day plan called for 15% EBITDA improvement, but margins are actually <span className="font-semibold text-red-600">eroding by 8%</span>.</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="bg-orange-50/50 rounded-xl border border-orange-100 p-4 mb-4">
                  <p className="text-sm text-slate-700">
                    Their acquisition is <span className="font-semibold text-orange-600">failing its 100-day plan</span>, putting the multi-million dollar investment at risk. High-visibility problem needing a fast solution.
                  </p>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-2">
                      <DollarSign className="w-5 h-5 text-red-500" />
                    </div>
                    <p className="font-semibold text-sm text-slate-800">Eroding Margins</p>
                    <p className="text-xs text-slate-400 mt-1">Supply chain chaos</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-2">
                      <TrendingDown className="w-5 h-5 text-amber-500" />
                    </div>
                    <p className="font-semibold text-sm text-slate-800">Flat Revenue</p>
                    <p className="text-xs text-slate-400 mt-1">Operational failures</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-2">
                      <Layers className="w-5 h-5 text-slate-500" />
                    </div>
                    <p className="font-semibold text-sm text-slate-800">Bad Integration</p>
                    <p className="text-xs text-slate-400 mt-1">Compounding issues</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Your Mission */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Mission Card */}
            <Card className="border-primary/30 overflow-hidden">
              <div className="bg-primary px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-white" />
                    <CardTitle className="text-white text-lg">Your Mission</CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedMission(!expandedMission)}
                    className="text-white/70 hover:text-white hover:bg-white/10 h-7 text-xs"
                    data-testid="read-more-mission"
                  >
                    {expandedMission ? (
                      <><ChevronUp className="w-3 h-3 mr-1" />Less</>
                    ) : (
                      <><ChevronDown className="w-3 h-3 mr-1" />About AEN</>
                    )}
                  </Button>
                </div>
              </div>
              <CardContent className="p-6 space-y-4">
                {expandedMission && (
                  <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100 animate-fade-in-up">
                    <h4 className="font-semibold text-primary mb-3">How AEN Works</h4>
                    <div className="space-y-3 text-sm">
                      <div>
                        <p className="font-medium text-slate-800 mb-1">The AEN Network</p>
                        <p className="text-slate-500 text-xs">Apex Executive Network maintains an exclusive, invitation-only network of under 500 C-suite operators and senior executives. Every member is rigorously vetted.</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-2 bg-white rounded-lg border border-slate-100">
                          <p className="font-medium text-primary text-xs mb-1">Pre-Acquisition</p>
                          <p className="text-slate-400 text-xs">Operators help PE firms during due diligence -- providing a "look under the hood" to validate operational aspects.</p>
                        </div>
                        <div className="p-2 bg-white rounded-lg border border-slate-100">
                          <p className="font-medium text-primary text-xs mb-1">Post-Acquisition</p>
                          <p className="text-slate-400 text-xs">AEN places operators into portfolio companies as Board Members, Interim CXOs, or strategic advisors.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-sm text-slate-600 italic">
                    "As a Senior Partner, you are their peer. Your stance is that of a <span className="text-primary font-semibold">trusted advisor</span>, not a vendor."
                  </p>
                </div>
                <p className="text-sm text-slate-500">
                  Position AEN as the <span className="text-primary font-semibold">only logical solution</span>. 
                  Focus on speed and financial impact -- not features.
                </p>
              </CardContent>
            </Card>

            {/* Talking Points Card */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base text-primary">AEN Talking Points</CardTitle>
                    <p className="text-xs text-slate-400">Guide your conversation</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                {[
                  { icon: Wrench, color: "emerald", title: "Execution, Not Advice", desc: "Operators who execute, not slide decks" },
                  { icon: Clock, color: "blue", title: "Speed to Impact", desc: "Weeks, not 6-9 months" },
                  { icon: Shield, color: "purple", title: "De-Risk Investment", desc: "Proven experience, solved before" },
                  { icon: Target, color: "amber", title: "Right Tool for the Job", desc: "Hands-on ops, not strategy" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                    <div className={`w-8 h-8 rounded-lg bg-${item.color}-50 flex items-center justify-center flex-shrink-0`}>
                      <item.icon className={`w-4 h-4 text-${item.color}-600`} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-slate-800">{item.title}</p>
                      <p className="text-xs text-slate-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Stakeholders */}
        <Card className="mb-10 border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Users className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Select Your Conversation Partner</CardTitle>
                  <p className="text-sm text-slate-400">Choose who you'll be meeting with</p>
                </div>
              </div>
              <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 font-medium">
                {selectedPersona === "jamie_sterling" ? "Jamie Sterling" : 
                 selectedPersona === "evelyn_reed" ? "Dr. Evelyn Reed" : "Ben Carter"} selected
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid md:grid-cols-3 gap-4">
              {scenario?.personas?.map((persona) => {
                const details = PERSONA_DETAILS[persona.id];
                const isSelected = selectedPersona === persona.id;
                const isExpanded = expandedPersona === persona.id;
                
                return (
                  <div 
                    key={persona.id} 
                    className={`relative rounded-xl border-2 transition-colors ${
                      isSelected 
                        ? "border-primary bg-primary/5" 
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <img
                          src={PERSONA_IMAGES[persona.id]}
                          alt={persona.name}
                          className={`w-14 h-14 rounded-full object-cover border-2 ${
                            isSelected ? "border-primary" : "border-slate-100"
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900">{persona.name}</h4>
                          <p className="text-xs text-slate-400">{persona.role.split(',')[0]}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className={`text-xs ${
                              persona.archetype.includes("Skeptic") ? "bg-red-50 text-red-600 border-red-200" :
                              persona.archetype.includes("Champion") ? "bg-emerald-50 text-emerald-600 border-emerald-200" :
                              "bg-amber-50 text-amber-600 border-amber-200"
                            }`}>
                              {persona.archetype.split('/')[0].trim()}
                            </Badge>
                            <Badge variant="outline" className={`text-xs ${getDifficultyColor(details.difficulty)}`}>
                              {details.difficulty}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {details.focus_areas.map((area, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-100">
                            {area}
                          </span>
                        ))}
                      </div>
                      {isExpanded && (
                        <div className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200 animate-fade-in-up">
                          <div className="mb-3">
                            <p className="data-label mb-1">Background</p>
                            <p className="text-sm text-slate-600">{details.background}</p>
                          </div>
                          <div className="mb-3">
                            <p className="data-label mb-1">Simulation Experience</p>
                            <p className="text-sm text-slate-600">{details.simulation_experience}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <p className="text-xs font-semibold text-emerald-600 mb-1">Positive Triggers</p>
                              <ul className="text-xs text-slate-500 space-y-0.5">
                                {details.trust_triggers.positive.map((t, i) => (
                                  <li key={i} className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" />{t}</li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-red-500 mb-1">Negative Triggers</p>
                              <ul className="text-xs text-slate-500 space-y-0.5">
                                {details.trust_triggers.negative.map((t, i) => (
                                  <li key={i} className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-400" />{t}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpanded(persona.id)}
                          className="flex-1 text-xs h-8"
                          data-testid={`read-more-${persona.id}`}
                        >
                          {isExpanded ? (
                            <><ChevronUp className="w-3 h-3 mr-1" />Less</>
                          ) : (
                            <><ChevronDown className="w-3 h-3 mr-1" />Read More</>
                          )}
                        </Button>
                        <Button
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedPersona(persona.id)}
                          className="flex-1 text-xs h-8"
                          data-testid={`select-${persona.id}`}
                        >
                          {isSelected ? (
                            <><Check className="w-3 h-3 mr-1" />Selected</>
                          ) : "Select"}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Phases */}
        <Card className="mb-10 border-slate-200 overflow-hidden">
          <div className="px-6 py-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                <span className="font-semibold text-sm text-slate-700">Simulation Phases</span>
              </div>
              <span className="text-xs text-slate-400">Progress through MEDDPICC methodology</span>
            </div>
          </div>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              {scenario?.phases?.map((phase, index) => {
                const colors = [
                  { border: "border-blue-200", bg: "bg-blue-50/50", badge: "bg-blue-100 text-blue-700" },
                  { border: "border-purple-200", bg: "bg-purple-50/50", badge: "bg-purple-100 text-purple-700" },
                  { border: "border-amber-200", bg: "bg-amber-50/50", badge: "bg-amber-100 text-amber-700" },
                  { border: "border-emerald-200", bg: "bg-emerald-50/50", badge: "bg-emerald-100 text-emerald-700" },
                ][index];
                return (
                  <div 
                    key={phase.id}
                    className={`flex-1 min-w-[160px] p-3 rounded-xl border ${colors.border} ${colors.bg}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${colors.badge}`}>
                        {phase.id}
                      </span>
                      <span className="text-xs text-slate-400">{phase.meddpicc_focus.join(", ")}</span>
                    </div>
                    <p className="font-medium text-slate-800 text-sm">{phase.name}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="bg-primary rounded-2xl p-8 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <div className="text-white text-left sm:text-left">
              <h3 className="text-xl font-bold">Ready to meet {selectedPersona === "jamie_sterling" ? "Jamie" : selectedPersona === "evelyn_reed" ? "Evelyn" : "Ben"}?</h3>
              <p className="text-sm text-blue-100">Put your MEDDPICC skills to the test</p>
            </div>
            <Button 
              size="lg" 
              className="bg-white text-primary hover:bg-blue-50 font-semibold px-8"
              onClick={handleStartSimulation}
              disabled={starting}
              data-testid="start-simulation-btn-bottom"
            >
              {starting ? (
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Play className="w-5 h-5 mr-2" />
              )}
              Start Simulation
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};
